// Cockpit revamp — Goals tab.
//
// Free-text goal + KPI cards with inline target editing. Clicking
// "edit target" turns the target value into an inline input; saving
// opens the ConfirmReasonModal so the change is captured into the
// audit log via PUT /campaigns/:id with `reason` threaded server-side.
import React, { useMemo, useState } from 'react';

import { Button, Callout, ConfirmReasonModal, Pill } from '../../../ui';
import {
  campaignsAPI,
  extractCampaignApiError,
} from '../../../services/campaignsAPI';
import type { Campaign } from '../../../types/campaign';
import {
  KPI_LABELS,
  type CampaignKpi,
  type CampaignKpiProgress,
  normalizeKpis,
} from '../CampaignGoalsKpis';

interface CampaignGoalsTabProps {
  campaign: Campaign;
  progress: CampaignKpiProgress[];
  /** True when the campaign hasn't been launched — actuals render as 0
   * with a "will start tracking after launch" hint. */
  isPreLaunch: boolean;
  onCampaignChange: (next: Campaign) => void;
  onAuditChanged: () => void;
}

interface PendingEdit {
  index: number;
  kpi: CampaignKpi;
  oldValue: number;
  newValue: number;
}

function formatValue(v: number, unit: string): string {
  if (unit === '$') {
    return v >= 1000
      ? `$${(v / 1000).toFixed(v >= 10000 ? 0 : 1)}k`
      : `$${v.toFixed(0)}`;
  }
  if (unit === '%') return `${v.toFixed(1)}%`;
  if (unit === 'days') return `${v.toFixed(1)}d`;
  return v.toString();
}

const CampaignGoalsTab: React.FC<CampaignGoalsTabProps> = ({
  campaign,
  progress,
  isPreLaunch,
  onCampaignChange,
  onAuditChanged,
}) => {
  const kpis = useMemo<CampaignKpi[]>(
    () => normalizeKpis(campaign.kpis),
    [campaign.kpis],
  );
  const progressByName = useMemo(() => {
    const m = new Map<string, CampaignKpiProgress>();
    for (const p of progress) m.set(p.name, p);
    return m;
  }, [progress]);

  const [editingIdx, setEditingIdx] = useState<number | null>(null);
  const [draft, setDraft] = useState('');
  const [pending, setPending] = useState<PendingEdit | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const startEdit = (index: number) => {
    setEditingIdx(index);
    setDraft(String(kpis[index].target_value));
    setError(null);
  };

  const cancelEdit = () => {
    setEditingIdx(null);
    setDraft('');
  };

  const submitEdit = () => {
    if (editingIdx === null) return;
    const next = parseFloat(draft);
    const old = kpis[editingIdx];
    if (!Number.isFinite(next) || next <= 0) {
      setError('Target must be a positive number.');
      return;
    }
    if (next === old.target_value) {
      cancelEdit();
      return;
    }
    setPending({ index: editingIdx, kpi: old, oldValue: old.target_value, newValue: next });
  };

  const confirmEdit = async (reason: string) => {
    if (!pending) return;
    setBusy(true);
    setError(null);
    try {
      const nextKpis = kpis.map((k, i) =>
        i === pending.index ? { ...k, target_value: pending.newValue } : k,
      );
      const updated = await campaignsAPI.update(campaign.id, {
        kpis: nextKpis,
        reason,
      });
      onCampaignChange(updated);
      onAuditChanged();
      setPending(null);
      setEditingIdx(null);
      setDraft('');
    } catch (e) {
      setError(extractCampaignApiError(e, 'Could not save target.'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div>
      {campaign.campaign_goals ? (
        <p className="text-sm text-slate-300 italic mb-4">"{campaign.campaign_goals}"</p>
      ) : (
        <p className="text-xs text-slate-500 italic mb-4">
          No goal statement set. Edit the campaign to add one.
        </p>
      )}

      {kpis.length === 0 ? (
        <p className="text-sm text-slate-500 italic px-3 py-6 text-center">
          No KPIs defined for this campaign.
        </p>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          {kpis.map((k, i) => {
            const editing = editingIdx === i;
            const prog = progressByName.get(k.name);
            const actual = isPreLaunch ? 0 : prog?.current_value ?? 0;
            const pct =
              k.target_value > 0
                ? Math.min(100, Math.round((actual / k.target_value) * 100))
                : 0;
            const overTarget = actual > k.target_value;
            const label = KPI_LABELS[k.name] ?? k.name;
            return (
              <div
                key={k.name}
                className="px-3 py-3 rounded-ds-md bg-surface-2 border border-white/[0.06]"
              >
                <div className="flex items-center justify-between mb-2">
                  <span className="text-[10px] font-mono uppercase tracking-wider text-slate-500">
                    {label}
                  </span>
                  {!editing ? (
                    <Button
                      intent="ghost"
                      size="sm"
                      onClick={() => startEdit(i)}
                      aria-label={`Edit target for ${label}`}
                    >
                      edit target
                    </Button>
                  ) : null}
                </div>
                <div className="flex items-baseline gap-1.5">
                  <span
                    className={`font-mono text-2xl font-semibold ${
                      isPreLaunch ? 'text-slate-500' : 'text-slate-100'
                    }`}
                  >
                    {formatValue(actual, k.unit)}
                  </span>
                  {editing ? (
                    <span className="inline-flex items-baseline gap-1">
                      <span className="text-xs text-slate-400 font-mono">/</span>
                      <input
                        type="number"
                        min="0.01"
                        step="any"
                        value={draft}
                        onChange={(e) => setDraft(e.target.value)}
                        autoFocus
                        className="bg-surface-1 border border-white/[0.12] rounded-ds-md px-2 py-0.5 text-xs w-20 text-slate-100 font-mono outline-none focus:border-accent"
                        aria-label={`New target for ${label}`}
                      />
                      <span className="text-xs text-slate-500 font-mono">
                        {k.unit}
                      </span>
                    </span>
                  ) : (
                    <span className="font-mono text-xs text-slate-500">
                      / {formatValue(k.target_value, k.unit)}
                    </span>
                  )}
                </div>
                <div className="h-1.5 mt-2 bg-white/[0.05] rounded-full overflow-hidden">
                  <div
                    className={`h-full ${overTarget ? 'bg-ok' : 'bg-accent'}`}
                    style={{ width: `${pct}%` }}
                  />
                </div>
                {editing ? (
                  <div className="flex gap-2 mt-3">
                    <Button intent="primary" size="sm" onClick={submitEdit}>
                      Save
                    </Button>
                    <Button intent="ghost" size="sm" onClick={cancelEdit}>
                      Cancel
                    </Button>
                  </div>
                ) : prog && !prog.computed ? (
                  <Pill intent="neutral" className="mt-2">
                    No data yet
                  </Pill>
                ) : null}
              </div>
            );
          })}
        </div>
      )}

      {error ? (
        <div className="mt-3 px-3 py-2 rounded-ds-md border border-danger-border bg-danger-tint text-danger-text text-xs">
          {error}
        </div>
      ) : null}

      <Callout tone="info" className="mt-4" title="Post-completion attribution">
        Even after this campaign reaches a terminal state, qualifications
        from pending callbacks continue rolling up here, marked{' '}
        <code className="font-mono">post_completion</code>.
      </Callout>

      <ConfirmReasonModal
        open={pending !== null}
        title={
          pending
            ? `Change ${KPI_LABELS[pending.kpi.name] ?? pending.kpi.name} target?`
            : ''
        }
        description={
          pending ? (
            <>
              Target moves from{' '}
              <strong>{formatValue(pending.oldValue, pending.kpi.unit)}</strong>{' '}
              to{' '}
              <strong>{formatValue(pending.newValue, pending.kpi.unit)}</strong>.
              The change is recorded in the audit log alongside your reason.
            </>
          ) : null
        }
        confirmLabel="Save target"
        confirmIntent="primary"
        saving={busy}
        error={error}
        onCancel={() => {
          setPending(null);
          setBusy(false);
        }}
        onConfirm={confirmEdit}
      />
    </div>
  );
};

export default CampaignGoalsTab;
